import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import logodark from "../../assets/images/logo-dark.png";
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { requestPasswordResetEmployeeAPI } from "../../helpers/api.ts";

const EmployeeForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email")
        .required("Please Enter Your Email"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        const response = await requestPasswordResetEmployeeAPI(values.email);
        if (response.success) {
          setSuccessMessage(response.message);
          // Store OTP request timestamp (10 minutes expiration)
          const otpTimestamp = Date.now();
          localStorage.setItem(`otp_timestamp_${values.email}`, otpTimestamp.toString());
          // Navigate to OTP verification page after 1 second
          setTimeout(() => {
            navigate("/verify-otp", { state: { email: values.email, userType: "employee" } });
          }, 1000);
        }
      } catch (error) {
        if (error.response) {
          const { data } = error.response;
          setErrorMessage(
            data.message || data.error || "An error occurred. Please try again."
          );
        } else {
          setErrorMessage("Unable to connect to the server. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    document.body.className = "bg-pattern";
    return () => {
      document.body.className = "";
    };
  }, []);

  return (
    <React.Fragment>
      <div className="bg-overlay"></div>
      <div className="account-pages my-5 pt-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              <Card>
                <CardBody className="p-4">
                  <div>
                    <div className="text-center">
                      <Link to="/">
                        <img
                          src={logodark}
                          alt="dark logo"
                          height="100"
                          className="auth-logo logo-dark mx-auto"
                        />
                        <img
                          src={logodark}
                          alt="light logo"
                          height="100"
                          className="auth-logo logo-light mx-auto"
                        />
                      </Link>
                    </div>
                    <h4 className="font-size-18 text-muted mt-2 text-center">
                      Forgot Password?
                    </h4>
                    <p className="mb-5 text-center">
                      Enter your email address and we'll send you an OTP to reset your password.
                    </p>
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {errorMessage && (
                        <Alert color="danger">
                          <div className="text-center">{errorMessage}</div>
                        </Alert>
                      )}
                      {successMessage && (
                        <Alert color="success">
                          <div className="text-center">{successMessage}</div>
                        </Alert>
                      )}
                      <Row>
                        <Col md={12}>
                          <div className="mb-4">
                            <Label className="form-label">Employee Email</Label>
                            <Input
                              name="email"
                              className="form-control"
                              placeholder="Enter email"
                              type="email"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              invalid={
                                validation.touched.email &&
                                validation.errors.email
                              }
                            />
                            {validation.touched.email &&
                              validation.errors.email && (
                                <FormFeedback type="invalid">
                                  {validation.errors.email}
                                </FormFeedback>
                              )}
                          </div>
                          <div className="d-grid mt-4">
                            <button
                              className="btn btn-primary waves-effect waves-light"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Sending...
                                </>
                              ) : (
                                "Send OTP"
                              )}
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                    <div className="text-center mt-4">
                      <Link to="/EmployeeLogin" className="text-muted">
                        <i className="mdi mdi-arrow-left"></i> Back to Login
                      </Link>
                    </div>
                    <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-2 mb-4 text-center mt-4">
                      <Link to="/privacy-policy" className="text-muted">
                        Privacy Policy
                      </Link>
                      <span className="text-muted d-none d-md-inline">|</span>
                      <Link to="/terms-conditions" className="text-muted">
                        Terms & Conditions
                      </Link>
                      <span className="text-muted d-none d-md-inline">|</span>
                      <Link to="/contact-us" className="text-muted">
                        Contact Us
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-5 text-center">
                <p className="text-white-50">
                  © {new Date().getFullYear()} Crafted with{" "}
                  <i className="mdi mdi-heart text-danger"></i> by Govindani
                  Infotech Pvt. Ltd.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

EmployeeForgotPassword.propTypes = {
  error: PropTypes.string,
};

export default EmployeeForgotPassword;

